import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  variant?: "dark" | "light";
  showSummary?: boolean;
  className?: string;
}

const pageWindow = (current: number, total: number): (number | "ellipsis")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages: (number | "ellipsis")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push("ellipsis");

  for (let page = start; page <= end; page += 1) pages.push(page);

  if (end < total - 1) pages.push("ellipsis");

  pages.push(total);

  return pages;
};

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  variant = "dark",
  showSummary = false,
  className = "",
}: Props) {
  const safeTotal = Math.max(1, totalPages);
  const isDark = variant === "dark";

  if (totalPages <= 1 && !showSummary) return null;

  const baseButton = `inline-flex h-9 items-center justify-center rounded-lg border text-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${
    isDark
      ? "border-zinc-700 text-zinc-300 hover:border-blue-500 hover:text-blue-300"
      : "border-zinc-200 text-zinc-600 hover:border-blue-500 hover:text-blue-600"
  }`;

  const activeButton = "border-blue-600 bg-blue-600 text-white";
  const summaryText = isDark ? "text-zinc-500" : "text-zinc-500";

  const showingFrom =
    pageSize && totalItems ? (currentPage - 1) * pageSize + 1 : undefined;
  const showingTo =
    pageSize && totalItems
      ? Math.min(currentPage * pageSize, totalItems)
      : undefined;

  return (
    <nav
      aria-label="Pagination"
      className={`mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${
        isDark ? "text-zinc-400" : "text-zinc-600"
      } ${className}`}
    >
      {showSummary && (
        <p className={`text-sm ${summaryText}`}>
          {totalItems && totalItems > 0 && showingFrom && showingTo
            ? `Showing ${showingFrom}–${showingTo} of ${totalItems}`
            : "No results"}
        </p>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 sm:justify-end">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className={`${baseButton} w-9`}
          >
            <ChevronLeft size={16} />
          </button>

          {pageWindow(currentPage, safeTotal).map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                aria-hidden="true"
                className="px-1 text-sm text-zinc-500"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
                className={`${baseButton} min-w-9 px-2.5 ${
                  page === currentPage ? activeButton : ""
                }`}
              >
                {page}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className={`${baseButton} w-9`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </nav>
  );
}
